import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { MacroData } from '@/types/settings-api';

/**
 * Canonical query-key factory. Same pattern as other settings hooks
 * so PR-B (save mutation) can invalidate this slot cleanly.
 */
export function macrosByProviderIdQueryKey(
  doctorSetupId: string | null | undefined,
  organizationId: string | null | undefined,
  search: string,
): readonly [string, string, string | null | undefined, string | null | undefined, string] {
  return [
    'settings',
    'macros-by-provider-id',
    doctorSetupId,
    organizationId,
    search,
  ];
}

/**
 * Provider-scoped macros list — PROD-764 PR-A (Macros tab read-only).
 *
 * GET /api/Doctor/GetMacrosByProviderId?organizationId={orgId}&providerId={doctorId}&search={q}
 *
 * Returns a BARE ARRAY `MacroData[]` — distinct from Profiles +
 * Templates which both ship `{ request, isDeleted, ... }` envelopes.
 *
 * **Wire-shape note**: this endpoint uses `providerId` (CORRECTLY
 * spelled — not the typo'd `practionerId` used on the Schedule tab's
 * query strings). Hook builds the URLSearchParams accordingly.
 *
 * **`macroValue` is stringified Delta JSON** — the React side parses
 * it via `JSON.parse` only when needed for editor display (PR-B).
 * The list-display surface (this PR) uses `macroValueFormatted`
 * (plaintext) so no parse is required.
 *
 * Visibility: tab is OrgAdmin-gated in the shell. The hook itself
 * doesn't enforce — relies on the parent guarding the tab.
 *
 * Flutter source: `settings_data_source_impl.dart:1477`
 * (`getMacrosByProviderId`).
 */
export function useMacrosByProviderId(
  doctorSetupId: string | null | undefined,
  organizationId: string | null | undefined,
  search: string = '',
) {
  return useQuery({
    queryKey: macrosByProviderIdQueryKey(doctorSetupId, organizationId, search),
    queryFn: ({ signal }) => {
      if (!doctorSetupId || !organizationId) {
        throw new Error(
          'useMacrosByProviderId called without doctorSetupId or organizationId',
        );
      }
      const qs = new URLSearchParams({
        organizationId,
        search,
        // `providerId` — correctly spelled, distinct from the typo'd
        // `practionerId` used on the Schedule tab's query strings.
        providerId: doctorSetupId,
      });
      return get<MacroData[]>(
        `/api/Doctor/GetMacrosByProviderId?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!doctorSetupId && !!organizationId,
  });
}
